/**
 * StudioPromos – ETT samlet tilbudsskjema for skapere.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, ExternalLink, Upload, X, QrCode } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TITLE_PLACEHOLDERS = [
  "30% / OnlyFans",
  "Live tonight",
  "VIP snap",
  "New post",
];

export default function StudioPromos() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [qrPath, setQrPath] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [uploadingQr, setUploadingQr] = useState(false);
  const [useExpiry, setUseExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [sendAsDm, setSendAsDm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const placeholderIdx = useMemo(() => Math.floor(Math.random() * TITLE_PLACEHOLDERS.length), []);
  const dateLocale = i18n.language === "en" ? "en-GB" : i18n.language === "sv" ? "sv-SE" : i18n.language === "da" ? "da-DK" : "nb-NO";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/creator-tilbud");
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    setTitle(""); setBody(""); setLinkUrl(""); setLinkLabel("");
    setQrPath(""); setQrUrl("");
    setUseExpiry(false); setExpiryDate(""); setSendAsDm(false);
  };

  const uploadQr = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("studio.promos.err_qr_too_big"));
      return;
    }
    setUploadingQr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/me/creator-promo-qr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setQrPath(r.data.qr_image_path);
      setQrUrl(r.data.url);
      toast.success(t("studio.promos.qr_upload_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("studio.promos.qr_upload_err"));
    } finally {
      setUploadingQr(false);
    }
  };

  const removeQr = () => { setQrPath(""); setQrUrl(""); };

  const submit = async () => {
    if (!title.trim()) { toast.error(t("studio.promos.err_title_required")); return; }
    if (!body.trim())  { toast.error(t("studio.promos.err_body_required"));  return; }
    if (!linkUrl.trim() && !qrPath) {
      toast.error(t("studio.promos.err_link_or_qr"));
      return;
    }

    if (sendAsDm && !window.confirm(t("studio.promos.dm_confirm"))) return;

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        send_as_dm: sendAsDm,
      };
      if (linkUrl.trim()) payload.link_url = linkUrl.trim();
      if (linkLabel.trim()) payload.link_label = linkLabel.trim();
      if (qrPath) payload.qr_image_path = qrPath;
      if (useExpiry && expiryDate) {
        payload.expires_at = new Date(expiryDate + "T23:59:59").toISOString();
      }
      const r = await api.post("/me/creator-tilbud", payload);
      if (r.data.dm_sent) {
        toast.success(t("studio.promos.saved_dm_ok", { n: r.data.dm_recipient_count }));
      } else if (r.data.dm_error) {
        toast.warning(t("studio.promos.saved_dm_warn", { err: r.data.dm_error }));
      } else {
        toast.success(t("studio.promos.saved_ok"));
      }
      reset();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("studio.promos.create_err"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("studio.promos.delete_confirm"))) return;
    try {
      await api.delete(`/me/creator-tilbud/${id}`);
      toast.success(t("studio.promos.deleted_ok"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("studio.promos.delete_err"));
    }
  };

  return (
    <section data-testid="studio-promos">
      <div className="panel-tight p-5 mb-6 space-y-4" data-testid="promo-create-form">
        <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{t("studio.promos.new_title")}</h2>
        <p className="text-sm text-[var(--cream-soft)]">{t("studio.promos.new_lead")}</p>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("studio.promos.title_label")}</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
            placeholder={t("studio.promos.title_placeholder", { example: TITLE_PLACEHOLDERS[placeholderIdx] })}
            className="input-editorial w-full" data-testid="promo-title" />
        </div>

        <div>
          <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">{t("studio.promos.body_label")}</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} rows={3}
            placeholder={t("studio.promos.body_placeholder")}
            className="input-editorial w-full" data-testid="promo-body" />
        </div>

        <div className="border border-white/10 p-4 space-y-3">
          <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)]">
            {t("studio.promos.where_label")} <span className="text-[var(--magenta)]">{t("studio.promos.where_hint")}</span>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)] block mb-1.5">{t("studio.promos.link_url_label")}</label>
            <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} maxLength={500}
              placeholder="https://..." className="input-editorial w-full" data-testid="promo-link-url" />
          </div>

          {linkUrl.trim() && (
            <div>
              <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)] block mb-1.5">
                {t("studio.promos.link_label_label")}
              </label>
              <input value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)} maxLength={60}
                placeholder={t("studio.promos.link_label_placeholder")} className="input-editorial w-full" data-testid="promo-link-label" />
            </div>
          )}

          <div>
            <label className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)] block mb-1.5">{t("studio.promos.qr_label")}</label>
            {qrUrl ? (
              <div className="flex items-center gap-3 p-3 border border-white/15 bg-black/40">
                <img src={qrUrl} alt="QR" className="w-16 h-16 object-contain bg-white p-1" />
                <span className="text-sm text-[var(--cream-soft)] flex-1">{t("studio.promos.qr_uploaded")}</span>
                <button onClick={removeQr} className="text-red-300 hover:text-red-200" data-testid="promo-qr-remove">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-sm text-[var(--cream-soft)] cursor-pointer hover:border-white/30">
                <Upload className="h-4 w-4" />
                {uploadingQr ? t("studio.promos.qr_uploading") : t("studio.promos.qr_choose")}
                <input type="file" accept="image/*" className="hidden"
                  disabled={uploadingQr}
                  onChange={(e) => uploadQr(e.target.files?.[0])}
                  data-testid="promo-qr-input" />
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={useExpiry} onChange={(e) => setUseExpiry(e.target.checked)}
              data-testid="promo-use-expiry" />
            <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)]">{t("studio.promos.expiry_label")}</span>
          </label>
          {useExpiry && (
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              className="input-editorial mt-2 w-48" data-testid="promo-expiry" />
          )}
        </div>

        <div className="border-t border-white/10 pt-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={sendAsDm} onChange={(e) => setSendAsDm(e.target.checked)}
              className="mt-1" data-testid="promo-send-dm" />
            <div>
              <span className="font-meta text-[10px] tracking-[0.15em] text-[var(--cream-soft)] block">
                {t("studio.promos.send_dm_label")}
              </span>
              <span className="text-xs text-[var(--cream-faint)] block mt-1">
                {t("studio.promos.send_dm_warning")}
              </span>
            </div>
          </label>
        </div>

        <button onClick={submit} disabled={submitting}
          className="btn-magenta disabled:opacity-40" data-testid="promo-publish">
          {submitting ? t("studio.promos.publishing") : t("studio.promos.publish")}
        </button>
      </div>

      <h3 className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-3">{t("studio.promos.your_offers")}</h3>
      {loading && <div className="text-sm text-[var(--cream-soft)]">{t("common.loading")}</div>}
      {!loading && items.length === 0 && (
        <div className="panel-tight p-6 text-center text-[var(--cream-soft)] font-fraunces italic">
          {t("studio.promos.empty")}
        </div>
      )}
      {!loading && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id} className="panel-tight p-4" data-testid={`promo-row-${p.id}`}>
              <div className="flex items-baseline gap-3 mb-1">
                {p.is_expired && <span className="font-meta text-[10px] text-red-300">{t("studio.promos.expired_badge")}</span>}
                <span className="ml-auto font-meta text-[10px] text-[var(--cream-faint)]">
                  {new Date(p.created_at).toLocaleDateString(dateLocale)}
                </span>
                <button onClick={() => remove(p.id)}
                  className="font-meta text-[10px] text-red-300 hover:text-red-200 inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> {t("studio.promos.delete")}
                </button>
              </div>
              <div className="font-fraunces italic text-lg text-[var(--cream)]">{p.title}</div>
              <div className="text-sm text-[var(--cream-soft)] whitespace-pre-line mt-1">{p.body}</div>
              <div className="flex items-center gap-3 mt-3">
                {p.link_url && (
                  <a href={p.link_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-[var(--magenta)] hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> {p.link_label || t("studio.promos.open_link")}
                  </a>
                )}
                {p.qr_url && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-[var(--cream-soft)]">
                    <QrCode className="h-3.5 w-3.5" /> {t("studio.promos.qr_attached")}
                  </span>
                )}
              </div>
              {p.expires_at && !p.is_expired && (
                <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-2">
                  {t("studio.promos.expires_at", { date: new Date(p.expires_at).toLocaleDateString(dateLocale) })}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
